// 2026-08-30 전면 교체 — 등급 심볼을 크레스트 방패(A안)에서 "3D-2 보석 파셋"으로 바꾼다.
// 알파 UI 확정 패키지(브랜치 origin/claude/tier-system-ui-design-v692ou, PR #141)에서 반입/각색
// (`docs/design/tier-ui-handoff.md` 4절 정본 — 문서 자체는 반입하지 않았다, 수치만 옮김).
//
// 정육각형을 6개 쐐기면으로 나누고 등급 단계 수만큼 채운다. 채운 면은 좌상단 광원 기준으로
// 밝기가 달라져 두께·그림자 없이 입체로 읽힌다. **채운 면의 개수가 곧 등급 단계**라 색을
// 구분 못 해도 순서를 읽을 수 있다(WCAG 1.4.1 — 색만으로 정보 전달 금지).
//
// prop API는 기존 크레스트 버전과 동일하게 유지한다(호출부 6곳 — MyTier 히어로 3곳/팝오버,
// AdminCreatorTiers TierBadge, ChannelTierBadge — 전부 무변경): tierName(한글) | band(S~D) |
// isRookie | size | ...rest. `dim`/`decorative`는 알파 스펙에 있는 prop이라 함께
// 받아두되(휴면 옅게 표시, aria-hidden 장식용) 현재 호출부는 아직 안 쓴다.
//
// 사용자 결정(2026-08-31): 티어 원색 전 위치 고정, 흰 단색(mono) 변형 폐기 — 알파 스펙 H-2
// "흰 단색" 문구에서 의도적 이탈, 알파 재확인 대상. 기존 `plate` prop(진한 배경에서 흰색
// 단색(mono)으로 렌더하던 매핑)은 완전히 제거했다 — 이제 어떤 배경 위에서도 항상
// `tierSymbols.js`/`colors.js`의 티어 고유색 그대로 그린다. 대신 진한 배경(브랜드 그라디언트
// 등) 위에서 심볼 실루엣이 옅어 보이는 문제는 색 자체를 바꾸지 않고 심볼 뒤에 옅은 흰
// 반투명 받침만 까는 `backdrop` prop으로 분리했다(아래 참고, 시인성 실측으로 필요한 호출부만
// 적용 — `docs/pages/main/MyTier.md` 참고).
//
// 사용자 결정(2026-08-31, 같은 날 두 번째): 24px 이하 "칩 배경 + 한 글자" 폴백(스펙 4절)을
// 폐기한다 — 알파 스펙에서의 의도적 이탈, 알파 재확인 대상. 배경: 추이 카드·팝오버·채널 배지처럼
// 작은 호출부(12~16px)마다 글자 칩만 보여 "티어 아이콘이 안 보인다"는 지적. 이제 어떤 크기에서도
// 항상 6면 파셋 육각형을 그린다. 다만 아주 작은 크기(TINY_DETAIL_MAX 이하)에서는 면 경계
// 컷팅 하이라이트(얇은 흰 선 장식)만 생략한다 — 이 크기에서 실제로 뭉개져 노이즈로 보이기
// 때문(색·채운 면 개수·외곽 점선 등 "정보"를 담은 요소는 전부 그대로 유지, 순수 장식만 생략).
import colors from "../theme/foundations/colors";
import { TIER_SYMBOL_META, TIER_COLOR_KEY } from "../lib/tierSymbols";
import { tierNameFromBand } from "../lib/creatorTierMetrics";
import { tt } from "../i18n/tt";

// 이 크기 이하에서는 면 컷팅 하이라이트(장식용 얇은 선)만 생략한다(위 주석 참고).
const TINY_DETAIL_MAX = 20;

// 좌상단 광원. 면 인덱스(12시부터 시계방향)별 밝기 계수 — 양수는 밝게, 음수는 어둡게(스펙 4절).
const LIGHT = [0.62, 0.34, 0.02, -0.22, -0.06, 0.3];

function hexToRgb(hex) {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

function rgbToHex(rgb) {
  return `#${rgb
    .map((v) => {
      const x = Math.max(0, Math.min(255, Math.round(v))).toString(16);
      return x.length < 2 ? `0${x}` : x;
    })
    .join("")}`;
}

// hex 두 개를 k(0~1)만큼 섞는다 — 파셋 명암을 만드는 유일한 계산.
function mix(from, to, k) {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  return rgbToHex(a.map((v, i) => v + (b[i] - v) * k));
}

// 중심 (cx, cy), 반지름 r 인 정육각형 꼭짓점. 12시부터 시계방향.
function hexPoints(cx, cy, r) {
  const pts = [];
  for (let i = 0; i < 6; i += 1) {
    const a = ((-90 + i * 60) * Math.PI) / 180;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return pts;
}

function toPointsAttr(points) {
  return points.map((q) => `${q[0].toFixed(2)},${q[1].toFixed(2)}`).join(" ");
}

// 등급 없음 자리 표시 — 채운 면이 없는 회색 점선 육각형. 색을 쓰지 않아 어떤 등급과도 혼동되지 않는다.
export function EmptyTierSymbol({ size = 24, decorative = false, ...rest }) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.42;
  const outline = toPointsAttr(hexPoints(cx, cy, r));
  const a11yProps = {
    role: decorative ? undefined : "img",
    "aria-label": decorative ? undefined : "등급 없음",
    "aria-hidden": decorative ? "true" : undefined,
  };
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} {...a11yProps} {...rest}>
      <polygon points={outline} fill={colors.neutral[100]} />
      <polygon
        points={outline}
        fill="none"
        stroke={colors.neutral[400]}
        strokeWidth={Math.max(1, size * 0.03)}
        strokeLinejoin="round"
        strokeDasharray={`${(size * 0.09).toFixed(1)} ${(size * 0.07).toFixed(1)}`}
      />
    </svg>
  );
}

export default function TierSymbol({
  tierName,
  band,
  isRookie,
  size = 24,
  backdrop = false,
  dim = false,
  decorative = false,
  placeholder = false,
  ...rest
}) {
  const key = isRookie ? "루키" : tierName || tierNameFromBand(band);
  const meta = key ? TIER_SYMBOL_META[key] : null;
  // 2026-09-02(대표 지시): 등급이 없는 채널의 심볼 자리가 빈 원으로 남아 "채널 로고가 안 보인다"로
  // 읽혔다. `placeholder` 를 주면 등급 없음 상태를 회색 점선 육각형(면 0개)으로 그려, 그 자리가
  // 로고가 아니라 **우리 등급을 표기하는 자리**임이 드러나게 한다. 기본값 false — 기존 호출부는
  // 그대로 null(아무것도 안 그림)이다.
  if (!meta) return placeholder ? <EmptyTierSymbol size={size} decorative={decorative} {...rest} /> : null;

  const colorKey = TIER_COLOR_KEY[key] || "rookie";
  const token = colors.tier[colorKey];
  const label = tt("a.6cd793b6", "{{v0}} 등급", { v0: key });
  const a11yProps = {
    role: decorative ? undefined : "img",
    "aria-label": decorative ? undefined : label,
    "aria-hidden": decorative ? "true" : undefined,
  };

  const base = token.base;
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.42;
  const points = hexPoints(cx, cy, r);
  const outline = toPointsAttr(points);
  // 아주 작은 크기(팝오버 배지·채널 배지 등 12~16px)에서는 면 컷팅 하이라이트가 실제로
  // 뭉개져 노이즈로 보이므로 그 장식만 생략한다(색·채운 면 개수·외곽선은 그대로).
  const tiny = size <= TINY_DETAIL_MAX;

  const wedges = [];
  for (let i = 0; i < 6; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % 6];
    const filled = i < meta.facets;

    let fill;
    if (filled) {
      fill = LIGHT[i] >= 0 ? mix(base, "#ffffff", LIGHT[i] * 0.55) : mix(base, "#243139", -LIGHT[i] * 0.9);
    } else {
      fill = mix(base, "#ffffff", 0.86);
    }

    wedges.push(
      <path
        key={`w${i}`}
        d={`M${cx} ${cy}L${a[0].toFixed(2)} ${a[1].toFixed(2)}L${b[0].toFixed(2)} ${b[1].toFixed(2)}Z`}
        fill={fill}
      />
    );

    // 채운 면에만 컷팅 하이라이트 — 면 경계를 드러내 입체감을 만든다(아주 작은 크기에서는 생략).
    if (filled && !tiny) {
      wedges.push(
        <path
          key={`h${i}`}
          d={`M${cx} ${cy}L${a[0].toFixed(2)} ${a[1].toFixed(2)}`}
          stroke="#ffffff"
          strokeWidth={Math.max(0.6, size * 0.014)}
          opacity={0.55}
        />
      );
    }
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} opacity={dim ? 0.45 : 1} {...a11yProps} {...rest}>
      {/* backdrop: 심볼 색은 절대 바꾸지 않고, 진한 배경(브랜드 그라디언트 등) 위에서 실루엣이
          옅어 보이는 문제만 옅은 흰 반투명 받침으로 보정한다(2026-08-31 사용자 결정 — 시인성
          실측으로 필요한 호출부에만 적용, `docs/pages/main/MyTier.md` 참고). */}
      {backdrop && <circle cx={cx} cy={cy} r={r * 1.18} fill="rgba(255,255,255,0.75)" />}
      <polygon points={outline} fill={mix(base, "#ffffff", 0.8)} />
      {wedges}
      <polygon
        points={outline}
        fill="none"
        stroke={mix(base, "#22323a", 0.24)}
        strokeWidth={Math.max(1, size * 0.03)}
        strokeLinejoin="round"
        strokeDasharray={meta.dashed ? `${(size * 0.09).toFixed(1)} ${(size * 0.07).toFixed(1)}` : undefined}
      />
      {meta.ring && (
        <polygon
          points={toPointsAttr(hexPoints(cx, cy, r * 1.2))}
          fill="none"
          stroke={colors.accent[500]}
          strokeWidth={Math.max(1, size * 0.024)}
          opacity={0.9}
        />
      )}
    </svg>
  );
}
